// components/LegalRepresentatives/TableauRepresentations.js
import { useState } from 'react';
import ReactDOM from 'react-dom';

// Composants
import AccountRepresentativesFilterBar from './AccountRepresentativesFilterBar';
import AccountRepresentativesTable from './AccountRepresentativesTable';
import RepresentativeDetailModal from './RepresentativeDetailModal';
import ManagedAccountsModal from './ManagedAccountsModal';
import RepresentativeDocumentsModal from './RepresentativeDocumentsModal'; // NOUVEAU IMPORT
import Pagination from './Pagination';
import { useAccountRepresentatives } from '../service/Hooks/useAccountRepresentatives';
import RemoveRepresentativeModal from './RemoveRepresentativeModal';
import { toast } from 'sonner';

// En-tête du modal
const ModalHeader = ({ title, onClose, loading, representativesCount }) => (
  <div className="modal-header bg-light text-white">
    <div className="d-flex align-items-center w-100 justify-content-between">
      <div className="d-flex align-items-center">
        <i className="fas fa-user-tie me-2 text-primary"></i>
        <h5 className="modal-title mb-0">{title}</h5>
        {representativesCount !== null && (
          <span className="badge bg-white text-primary ms-2">
            {representativesCount} représentant(s)
          </span>
        )}
      </div>
      <button
        type="button"
        className="btn-close btn-close-primary"
        onClick={onClose}
        disabled={loading}
      ></button>
    </div>
  </div>
);

const TableauRepresentations = ({ account, accountId, onClose }) => {
  const {
    representatives,
    pagination,
    loading,
    error,
    filters,
    updateFilters,
    handlePageChange,
    resetFilters
  } = useAccountRepresentatives(accountId);

  // États pour les modals
  const [detailModal, setDetailModal] = useState({
    isOpen: false,
    representative: null
  });

  const [managedAccountsModal, setManagedAccountsModal] = useState({
    isOpen: false,
    representative: null
  });

  // NOUVEL ÉTAT : Modal des documents
  const [documentsModal, setDocumentsModal] = useState({
    isOpen: false,
    representative: null
  });

  /**
   * Ouvrir le modal des détails
   * @param {Object} representative - Représentant sélectionné
   */
  const handleViewDetails = (representative) => {
    // Passer directement le représentant avec son ID
    // Le modal va maintenant charger les détails via l'API
    setDetailModal({
      isOpen: true,
      representative: {
        id: representative.id // L'ID est nécessaire pour l'API
      }
    });
  };

  /**
   * Ouvrir le modal des comptes gérés
   * @param {Object} representative - Représentant sélectionné
   */
  const handleViewManagedAccounts = (representative) => {
    const representativeForModal = {
      ...representative,
      accountId: representative.representativeAccountId,
      displayName: representative.representativeDisplayName
    };

    setManagedAccountsModal({
      isOpen: true,
      representative: representativeForModal
    });
  };

  /**
   * NOUVELLE FONCTION : Ouvrir le modal des documents
   * @param {Object} representative - Représentant sélectionné
   */
  const handleViewDocuments = (representative) => {
    setDocumentsModal({
      isOpen: true,
      representative
    });
  };

  // Dans TableauRepresentations.js, ajoutez simplement :
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [selectedRepresentative, setSelectedRepresentative] = useState(null);

  // Fonction pour ouvrir le modal
  const handleRemoveRepresentative = (rep) => {
    setSelectedRepresentative({
      id: rep.id, // Garder l'ID du mandat pour l'affichage
      representativeAccountId: rep.representativeAccountId, // <-- AJOUTER CETTE LIGNE
      displayName: rep.representativeDisplayName || rep.representativeEmail,
    });
    setShowRemoveModal(true);
  };

  // Fonction pour supprimer tous
  const handleRemoveAll = () => {
    setSelectedRepresentative(null);
    setShowRemoveModal(true);
  };

  const handleRemoveSuccess = (result) => {
    // Rafraîchir les données
    if (resetFilters) {
      resetFilters();
    }
    
    // Message de succès contextuel
    const { data } = result;
    let message = result.message;
    
    if (data.accountUpdated) {
      if (data.emailUpdated) {
        message += ` • Compte converti avec email: ${data.currentEmail}`;
      } else if (data.requiresEmail) {
        message += ` • Email requis pour le compte`;
      } else {
        message += ` • Compte converti en non-administré`;
      }
    }
    
    // Vous pouvez ajouter un toast ici si vous voulez
    console.log('Dissociation réussie:', message);
    
    setShowRemoveModal(false);
  };


  const title = `Représentants Légaux - ${account?.companyName || account?.firstName + ' ' + account?.lastName || 'Compte'}`;

  return (
    <>
      {ReactDOM.createPortal(
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable modal-custom">
            
            <div className="modal-content">
              <ModalHeader 
                title={title} 
                onClose={onClose}
                loading={loading}
                representativesCount={pagination?.totalCount}
              />
              
              <div className="modal-body">
                {/* // Dans le rendu, ajoutez le bouton "Dissocier tous" */}
                <div className="d-flex justify-content-between mb-3">
                  <h5>Représentants légaux</h5>
                  <button className="btn btn-danger btn-sm" onClick={handleRemoveAll}>
                    Dissocier tous
                  </button>
                </div>
                {/* Message d'erreur */}
                {error && (
                  <div className="alert alert-danger d-flex align-items-center">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    <div>{error}</div>
                  </div>
                )}

                {/* Barre de filtres */}
                <AccountRepresentativesFilterBar
                  filters={filters}
                  onFiltersChange={updateFilters}
                  onReset={resetFilters}
                  loading={loading}
                />

                {/* Tableau des représentants */}
                <AccountRepresentativesTable
                  representatives={representatives}
                  loading={loading}
                  onViewDetails={handleViewDetails}
                  onViewManagedAccounts={handleViewManagedAccounts}
                  onViewDocuments={handleViewDocuments}
                  onRemoveRepresentative={handleRemoveRepresentative} 
                />

                {/* Pagination */}
                {pagination && pagination.totalPages > 1 && (
                  <div className="mt-4">
                    <Pagination
                      currentPage={pagination.pageNumber}
                      totalPages={pagination.totalPages}
                      totalCount={pagination.totalCount}
                      pageSize={pagination.pageSize}
                      onPageChange={handlePageChange}
                      loading={loading}
                    />
                  </div>
                )}

                {/* Aucun résultat */}
                {!loading && representatives.length === 0 && (
                  <div className="text-center py-5">
                    <i className="fas fa-users fa-3x text-muted mb-3"></i>
                    <h5 className="text-muted">Aucun représentant trouvé</h5>
                    <p className="text-muted">
                      Aucun représentant légal ne correspond à vos critères de recherche.
                    </p>
                  </div>
                )}
              </div>

              {/* Pied de page */}
              <div className="modal-footer">
                <div className="w-100 d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    {pagination && (
                      <>
                        Affichage de {representatives.length} sur {pagination.totalCount} représentant(s)
                      </>
                    )}
                  </small>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onClose}
                  >
                    <i className="fas fa-times me-1"></i>
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal des détails */}
      <RepresentativeDetailModal
        isOpen={detailModal.isOpen}
        onClose={() => setDetailModal({ isOpen: false, representative: null })}
        representative={detailModal.representative}
      />

      {/* Modal des comptes gérés */}
      <ManagedAccountsModal
        isOpen={managedAccountsModal.isOpen}
        onClose={() => setManagedAccountsModal({ isOpen: false, representative: null })}
        representative={managedAccountsModal.representative}
      />

      {/*  : Documents du représentant */}
      <RepresentativeDocumentsModal
        isOpen={documentsModal.isOpen}
        onClose={() => setDocumentsModal({ isOpen: false, representative: null })}
        representative={documentsModal.representative}
      />

      {/* Modal de dissociation - AUSSI avec createPortal */}
      {showRemoveModal && ReactDOM.createPortal(
        <RemoveRepresentativeModal
          isOpen={showRemoveModal}
          onClose={() => setShowRemoveModal(false)}
          account={account}
          representative={selectedRepresentative}
          onSuccess={handleRemoveSuccess}
          refreshData={() => {
            // FORCER le rechargement des représentants
            if (updateFilters) {
              updateFilters({}); // Recharge avec les filtres actuels
            }
            // Vous pouvez aussi ajouter un toast de confirmation
            toast.success('Liste des représentants actualisée');
          }}
        />,
        document.body
      )}
    </>
  );
};

export default TableauRepresentations;